import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useSoundEffects, SoundType, SoundConfig } from '../hooks/useSoundEffects.ts';

interface SoundContextType {
  playSound: (soundType: SoundType, config?: SoundConfig) => void;
  stopSound: (soundType: SoundType) => void;
  stopAllSounds: () => void;
  masterVolume: number;
  setMasterVolume: (volume: number) => void;
  isMuted: boolean;
  toggleMute: () => void;
  soundEnabled: boolean;
  setSoundEnabled: (enabled: boolean) => void;
  playCategorySound: (categoryId: string) => void;
}

const SoundContext = createContext<SoundContextType | undefined>(undefined);

export const useSound = () => {
  const context = useContext(SoundContext);
  if (!context) {
    throw new Error('useSound must be used within a SoundProvider');
  }
  return context;
};

interface SoundProviderProps {
  children: ReactNode;
}

export const SoundProvider: React.FC<SoundProviderProps> = ({ children }) => {
  const soundEffects = useSoundEffects();
  const [masterVolume, setMasterVolumeState] = useState<number>(0.7);
  const [soundEnabled, setSoundEnabledState] = useState<boolean>(true);

  // Charger les préférences depuis localStorage
  useEffect(() => {
    const savedVolume = localStorage.getItem('gameVolume');
    const savedSoundEnabled = localStorage.getItem('soundEnabled');
    
    if (savedVolume) {
      const volume = parseFloat(savedVolume);
      setMasterVolumeState(volume);
      soundEffects.setMasterVolume(volume);
    }
    
    if (savedSoundEnabled !== null) {
      const enabled = savedSoundEnabled === 'true';
      setSoundEnabledState(enabled);
      if (!enabled) {
        soundEffects.toggleMute();
      }
    }
  }, [soundEffects]);

  const setMasterVolume = (volume: number) => {
    setMasterVolumeState(volume);
    soundEffects.setMasterVolume(volume);
    localStorage.setItem('gameVolume', volume.toString());
  };

  const setSoundEnabled = (enabled: boolean) => {
    setSoundEnabledState(enabled);
    localStorage.setItem('soundEnabled', enabled.toString());
    
    if (enabled && soundEffects.isMuted) {
      soundEffects.toggleMute();
    } else if (!enabled && !soundEffects.isMuted) {
      soundEffects.toggleMute();
    }
  };

  const toggleMute = () => {
    soundEffects.toggleMute();
    const newMutedState = !soundEffects.isMuted;
    setSoundEnabledState(newMutedState);
    localStorage.setItem('soundEnabled', newMutedState.toString());
  };

  // Fonction helper pour jouer les sons de catégorie
  const playCategorySound = (categoryId: string) => {
    if (!soundEnabled) return;

    // Son spécial pour mystery, sinon toujours le son de géographie (son préféré !)
    const soundType = categoryId === 'mystery' ? 'categoryMystery' : 'categoryGeography';
    soundEffects.playSound(soundType);
  };

  const playSound = (soundType: SoundType, config?: SoundConfig) => {
    if (soundEnabled) {
      soundEffects.playSound(soundType, config);
    }
  };

  return (
    <SoundContext.Provider value={{
      playSound,
      stopSound: soundEffects.stopSound,
      stopAllSounds: soundEffects.stopAllSounds,
      masterVolume,
      setMasterVolume,
      isMuted: soundEffects.isMuted,
      toggleMute,
      soundEnabled,
      setSoundEnabled,
      playCategorySound
    }}>
      {children}
    </SoundContext.Provider>
  );
};
